/**
 * SYM-6367. The ad the server made for a finished video, fetched instead of copied.
 *
 * ⚠️ THE SERVER ALREADY MAKES THE AD. When a video reaches "completed" the API turns it into an ad
 * variant itself, once per video (VideoGenerationCompletedSideEffectsService in sympl-api). The ad
 * builder used to put the video into its own list as well -- as a new unsaved ad for Bring to Life,
 * onto the current ad for the classic video -- and the next Save sent that copy to the server as a
 * second ad. Every video then showed twice. Measured on production, 30 Sep 2026, campaign 8415: ads
 * 48875 and 48876 on the same file, 23 seconds apart, the second one made by Save.
 *
 * So the builder now only shows what the server made. The server makes it in a background job, a
 * second or two AFTER the video reports finished, so it is asked for a few times rather than once.
 */

interface AdWithFile {
  id?: number | null;
  path?: { id?: number | null } | null;
}

/** Ask this often, and for this long, before giving up on the server's ad. */
export const SERVER_AD_POLL_MS = 1_500;
export const SERVER_AD_ATTEMPTS = 40;

/**
 * The server's ad that carries this video file, if it is there yet.
 *
 * Matched on the file, because that is the one thing the finished video and the ad share: the ad is
 * created pointing at the video's own file.
 */
export function serverAdForVideo<T extends AdWithFile>(
  ads: readonly T[],
  videoFileId: number
): T | undefined {
  return ads.find(
    (ad) => typeof ad.id === 'number' && ad.path?.id === videoFileId
  );
}

/**
 * Keep asking for the campaign's ads until the one for this video appears.
 *
 * Returns undefined when it never does within the attempts, which means the server's background job
 * is late or failed; the caller says so rather than inventing an ad of its own, because inventing
 * one is exactly the duplicate this replaces.
 */
export async function waitForServerVideoAd<T extends AdWithFile>(
  fetchAds: () => Promise<readonly T[]>,
  videoFileId: number,
  {
    attempts = SERVER_AD_ATTEMPTS,
    intervalMs = SERVER_AD_POLL_MS,
    sleep = (ms: number) =>
      new Promise<void>((resolve) => setTimeout(resolve, ms)),
  }: {
    attempts?: number;
    intervalMs?: number;
    sleep?: (ms: number) => Promise<void>;
  } = {}
): Promise<T | undefined> {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (attempt > 0) await sleep(intervalMs);
    try {
      const found = serverAdForVideo(await fetchAds(), videoFileId);
      if (found) return found;
    } catch (_) {
      // One failed request is not a verdict; the next attempt asks again.
    }
  }
  return undefined;
}
